import React, { RefObject } from "react";
import { RouteComponentProps } from "react-router-dom";
import { APIError, EconomicIndicator, FormFieldConfig } from "../helpers/shared-interfaces";
import { generateDisplayErrorMessage, handleValidationErrors, mapAPIErrorToFormError, request, responseHandler } from "../helpers/utilities";
import * as Sentry from "@sentry/browser";
import { Button, Col, FormInstance, Row, Table, message } from "antd";
import Card from "../components/Card";
import EntitledSection from "../components/EntitledSection";
import entitlements from "../config/entitlements";
import { PlusOutlined } from "@ant-design/icons";
import { ColumnType } from "antd/lib/table";
import { CREATE_ECONOMIC_INDICATOR, GET_ECONOMIC_INDICATORS } from "../gql/economicIndicators";
import ModalCreator from "../components/ModalCreator";
import BaseForm from "../components/Form/BaseForm";
import { AxiosResponse } from "axios";
import { PageToolbar } from "../components/PageToolbar";

interface State {
  isLoading: boolean;
  economicIndicators: EconomicIndicator[];
  page: number;
  pageSize: number;
  economicIndicatorsCount: number;
  id?: number;
  showNewEconomicIndicatorModal: boolean;
  isCreatingEconomicIndicator: boolean;
}

export function getEconomicIndicatorsColumn<T extends EconomicIndicator>(
  economicIndicators: EconomicIndicator[],
): ColumnType<T>[] {
  return [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render(text: string): JSX.Element {
        return <span>{text}</span>;
      },
    },
    {
      title: "Inflation Rate %",
      dataIndex: "inflation",
      key: "inflation",
      render(text: string): JSX.Element {
        return <span>{text}</span>;
      },
    },
    {
      title: "Treasury Bill Rate %",
      dataIndex: "tbill",
      key: "tbill",
      render(text: string): JSX.Element {
        return <span>{text}</span>;
      },
    },
  ];
}

export const NewEconomicIndicatorUI = (props: {
  reference: React.RefObject<FormInstance<any>>;
}): JSX.Element => {
  const fields: FormFieldConfig[] = [
    {
      id: "date",
      name: "date",
      rule: "object",
      inputType: "defaultDate",
      required: true,
      label: "Date",
      placeholder: "Date",
      elementStyle: { width: "100%" },
      colConfig: {
        span: 24,
      },
    },
    {
      name: "inflation",
      rule: "number",
      inputType: "number",
      required: true,
      label: "Inflation Rate %",
      placeholder: "Inflation Rate %",
      elementStyle: { width: "100%" },
      colConfig: {
        span: 24,
      },
    },
    {
      name: "tbill",
      rule: "number",
      inputType: "number",
      required: true,
      label: "Treasury Bill Rate %",
      placeholder: "Treasury Bill Rate %",
      elementStyle: { width: "100%" },
      colConfig: {
        span: 24,
      },
    },
  ];

  return (
    <BaseForm
      layout="vertical"
      reference={props.reference}
      fieldsToHide={[]}
      fields={fields}
    />
  );
};

export default class EconomicIndicators extends React.Component<
  RouteComponentProps,
  State
> {
  reference: RefObject<FormInstance>;
  constructor(props: Readonly<RouteComponentProps>) {
    super(props);
    this.state = {
      isLoading: true,
      economicIndicators: [],
      page: 1,
      pageSize: 25,
      economicIndicatorsCount: 0,
      showNewEconomicIndicatorModal: false,
      isCreatingEconomicIndicator: false
    };
    this.reference = React.createRef<FormInstance>();
    this.nextPage = this.nextPage.bind(this);
    this.createEconomicIndicator = this.createEconomicIndicator.bind(this);
    this.toggleCreateEconomicIndicatorModal =
      this.toggleCreateEconomicIndicatorModal.bind(this);
  }

  async componentDidMount(): Promise<void> {
    await this.getEconomicIndicators();
  }

  toggleCreateEconomicIndicatorModal(): void {
    this.setState((prevState) => ({
      showNewEconomicIndicatorModal: !prevState.showNewEconomicIndicatorModal
    }));
  }

  async nextPage(page: number, pageSize: number): Promise<void> {
    this.setState(
      {
        page,
        pageSize,
      },
      async () => {
        await this.getEconomicIndicators();
      },
    );
  }

  getEconomicIndicators = async (): Promise<void> => {
    this.setState({ isLoading: true });
    const { page } = this.state;

    try {
      const { data }: any = await request({
        query: GET_ECONOMIC_INDICATORS,
        variables: { page },
      });
      this.setState({
        economicIndicators: data.data?.economicIndicators?.results || [],
        economicIndicatorsCount: data.data?.economicIndicators?.count || 0,
      });
    } catch (error) {
      message.error(`Failed To Load Economic Indicators - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  redirectToViewEconomicIndicatorPage(): void {
    this.props.history.push(`/economic-indicators/${this.state.id}`);
  }

  async createEconomicIndicator(): Promise<void> {
    interface Response {
      economicIndicator: EconomicIndicator;
      errors: APIError[];
    }

    if (!this.reference.current) return;
    let data;
    try {
      data = await this.reference.current.validateFields([
        "date",
        "inflation",
        "tbill",
      ]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    try {
      const response: AxiosResponse<Response> = await request({
        query: CREATE_ECONOMIC_INDICATOR,
        variables: data,
      });

      const { err, handled, mutation } = responseHandler<Response>(
        response,
        "createEconomicIndicator",
      );

      if (handled) {
        message.error("An Error Occurred. Your request couldn't be processed.");
        return;
      } else if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.reference.current.setFields(err.map(mapAPIErrorToFormError));
      } else {
        this.setState({ id: mutation?.economicIndicator?.id }, () => {
          message.success("Economic indicator created sucessfully");
          this.redirectToViewEconomicIndicatorPage();
        });
      }
    } catch (error) {
      message.error(`Failed to create category - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
      this.toggleCreateEconomicIndicatorModal();
    }
  }

  render(): JSX.Element | null {
    const { isLoading, economicIndicators, page, pageSize, economicIndicatorsCount, showNewEconomicIndicatorModal, isCreatingEconomicIndicator } =
      this.state;

    const resultsWithKeys: EconomicIndicator[] = economicIndicators.map(
      (result: EconomicIndicator): EconomicIndicator => {
        return {
          ...result,
          id: result.id,
        };
      },
    );

    return (
      <Card>
        <div className="categories-container">
          <PageToolbar modelName="economic indicators"
            totalCount={economicIndicatorsCount}
            currentDataCount={economicIndicators.length}
            page={page}
            pageSize={pageSize}
            primaryAction={
              <EntitledSection
                entitlement={entitlements.EconomicIndicators.create}
              >
                <Button
                  className="base-button"
                  size="large"
                  data-testid="create-category-button"
                  onClick={this.toggleCreateEconomicIndicatorModal}
                >
                  <PlusOutlined />
                  Create Economic Indicator
                </Button>
              </EntitledSection>
            }
          />

          <Row>
            <Col span={24}>
              <Table
                loading={isLoading}
                scroll={{ x: 400 }}
                columns={getEconomicIndicatorsColumn(economicIndicators)}
                dataSource={resultsWithKeys}
                onRow={(record) => ({
                  onClick: () => {
                    this.props.history.push({
                      pathname: `/economic-indicators/${record.id}`,
                      state: { economicIndicators },
                    });
                  },
                  style: { cursor: 'pointer' }

                })}
                pagination={{
                  current: page,
                  defaultPageSize: 25,
                  total: economicIndicatorsCount,
                  onChange: this.nextPage,
                  showSizeChanger: false,
                }}
              />
            </Col>
          </Row>
          {showNewEconomicIndicatorModal &&
            ModalCreator({
              title: "Create Asset Allocation Item",
              okType: "primary",
              okText: "Create",
              showModal: showNewEconomicIndicatorModal,
              onOkay: this.createEconomicIndicator,
              toggleModal: this.toggleCreateEconomicIndicatorModal,
              inscription: NewEconomicIndicatorUI({
                reference: this.reference,
              }),
            })}
        </div>
      </Card>
    );
  }
}
